import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { LoadingButton } from '@/components/LoadingButton';
import { Switch } from '@/components/ui/switch';
import {
  useGetMeNotificationSettings,
  usePutMeNotificationSettings,
  getGetMeNotificationSettingsQueryKey,
} from '@/api/auth/self/self';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Notification event names that can be configured
 */
const NOTIFICATION_EVENTS = {
  billing: ['BillingReminder'] as const,
  dataMission: ['DataMissionSyncFailed'] as const,
  marketing: ['ProductUpdates', 'NewsTrainingArticles'] as const,
};

type NotificationEvent =
  | (typeof NOTIFICATION_EVENTS.billing)[number]
  | (typeof NOTIFICATION_EVENTS.dataMission)[number]
  | (typeof NOTIFICATION_EVENTS.marketing)[number];

/**
 * User email notification settings card component
 * Allows users to configure which email notifications they want to receive
 */
export function UserEmailNotificationSettingsCard() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // Local state for notification settings (default transactional to true, marketing to false)
  const [settings, setSettings] = useState<Record<NotificationEvent, boolean>>({
    BillingReminder: true,
    DataMissionSyncFailed: true,
    ProductUpdates: false,
    NewsTrainingArticles: false,
  });

  // Track if user has made changes
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current notification settings
  const { data: notificationSettingsResponse, isLoading } = useGetMeNotificationSettings();

  // Update mutation
  const updateMutation = usePutMeNotificationSettings();

  // Sync fetched settings with local state
  useEffect(() => {
    if (notificationSettingsResponse?.data.settings) {
      const fetchedSettings = { ...settings };
      const marketingEvents = NOTIFICATION_EVENTS.marketing as readonly string[];
      for (const setting of notificationSettingsResponse.data.settings) {
        if (setting.eventName && setting.eventName in fetchedSettings) {
          // Marketing events default to false, others default to true
          const defaultValue = marketingEvents.includes(setting.eventName) ? false : true;
          fetchedSettings[setting.eventName as NotificationEvent] = setting.isEnabled ?? defaultValue;
        }
      }
      setSettings(fetchedSettings);
      setHasChanges(false);
    }
  }, [notificationSettingsResponse]);

  /**
   * Handle toggle change for a notification setting
   */
  const handleToggle = (eventName: NotificationEvent, checked: boolean) => {
    setSettings((prev) => ({
      ...prev,
      [eventName]: checked,
    }));
    setHasChanges(true);
  };

  /**
   * Save notification settings
   */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Convert settings to API format
      const settingsArray = Object.entries(settings).map(([eventName, isEnabled]) => ({
        eventName,
        isEnabled,
      }));

      await updateMutation.mutateAsync({
        data: {
          settings: settingsArray,
        },
      });

      // Invalidate and refetch notification settings
      await queryClient.invalidateQueries({
        queryKey: getGetMeNotificationSettingsQueryKey(),
      });

      toast.success(t.settings.emailNotifications.updateSuccess);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      toast.error(t.settings.emailNotifications.updateError);
    }
  };

  /**
   * Render a notification toggle with label and description
   */
  const renderNotificationToggle = (eventName: NotificationEvent) => {
    const translations = t.settings.emailNotifications.events[eventName];

    return (
      <div key={eventName} className="flex items-start justify-between gap-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor={eventName} className="text-sm font-medium">
            {translations.label}
          </Label>
          <p className="text-sm text-muted-foreground">{translations.description}</p>
        </div>
        <Switch
          id={eventName}
          checked={settings[eventName]}
          onCheckedChange={(checked) => { handleToggle(eventName, checked); }}
          disabled={isLoading || updateMutation.isPending}
        />
      </div>
    );
  };

  /**
   * Render a section with heading and notification toggles
   */
  const renderSection = (
    titleKey: 'general' | 'billing' | 'dataMission' | 'marketing',
    events: readonly NotificationEvent[]
  ) => {
    const sectionTitles = {
      general: t.settings.emailNotifications.sections.general,
      billing: t.settings.emailNotifications.sections.billing,
      dataMission: t.settings.emailNotifications.sections.dataMission,
      marketing: t.settings.emailNotifications.sections.marketing,
    };

    return (
      <div className="space-y-1">
        <h4 className="text-sm font-semibold text-muted-foreground">{sectionTitles[titleKey]}</h4>
        <div>{events.map((event) => renderNotificationToggle(event))}</div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.settings.emailNotifications.title}</CardTitle>
        <CardDescription>{t.settings.emailNotifications.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => { void handleSave(e); }} className="space-y-6">
          {renderSection('billing', NOTIFICATION_EVENTS.billing)}
          {renderSection('dataMission', NOTIFICATION_EVENTS.dataMission)}
          {renderSection('marketing', NOTIFICATION_EVENTS.marketing)}

          <div className="flex justify-end pt-4">
            <LoadingButton
              type="submit"
              disabled={!hasChanges || isLoading}
              isLoading={updateMutation.isPending}
            >
              {t.settings.emailNotifications.save}
            </LoadingButton>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
